import { EntityType } from "../../Common.types";
import { isNullOrWhitespace } from "../../stringOperations";
import { useDotLegalFileUploadDataMapping } from "../dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { isPreviewSupported } from "./DocumentPreview.hooks";
import { DocumentPreviewable, PreviewableDocument } from "./DocumentPreview.types";
import { buildDocumentPreviewUrl } from "./DocumentPreview.urls";
import { usePanes } from "../../../main/panes/usePanes";
import DocumentPane, { DocumentPaneProps } from "../../../main/panes/documentPane/DocumentPane";

export interface UseDocumentPreviewManagerProps {
    documents: Array<DocumentPreviewable>;
    entityType?: EntityType;
    entityId?: string;
    entityTitle?: string;
    /** What this caller wants decided about the pane its previews open as, beyond the documents themselves. */
    documentEntry?: Omit<DocumentPaneProps, "documents" | "initialDocumentId">;
    /**
     * Opens the preview beside the pane that asked for it rather than over it: that pane moves into the rail
     * while the rail is free, and is replaced as usual when it is not. For a pane on the stage showing a
     * document of its own, such as a task and its documentation.
     */
    openBesideCurrentPane?: boolean;
}

export function useDocumentPreviewManager(props: UseDocumentPreviewManagerProps) {
    const { downloadDocumentVersion, downloadDocumentVersionByVersionId } = useDotLegalFileUploadDataMapping();
    const { open: openPane, openBeside } = usePanes();

    const previewableDocuments: Array<PreviewableDocument> = props.documents
        .filter((doc) => !isNullOrWhitespace(doc.fileName) && isPreviewSupported(doc.fileName!))
        .map((doc) => ({
            documentId: doc.documentId,
            dataId: doc.dataId,
            documentVersionId: doc.documentVersionId,
            entityType: (doc.entityType ?? props.entityType)!,
            entityId: (doc.entityId ?? props.entityId)!,
            fileName: doc.fileName!,
            name: doc.name,
            entityTitle: doc.entityTitle ?? props.entityTitle,
            isTooLargeForAi: doc.isTooLargeForAi,
        }));

    const handleDocumentClick = async (document: DocumentPreviewable) => {
        handleDocumentWithTypeAndIdClick(document, document.entityType ?? props.entityType!, document.entityId ?? props.entityId!);
    };

    const showPreview = (document: DocumentPreviewable) => {
        const initialDocumentId = document.documentVersionId ?? document.documentId;
        const paneProps: DocumentPaneProps = { documents: previewableDocuments, initialDocumentId, ...props.documentEntry };
        if (props.openBesideCurrentPane) {
            openBeside(DocumentPane, paneProps);
            return;
        }
        openPane(DocumentPane, paneProps);
    };

    const handleDocumentWithTypeAndIdClick = async (document: DocumentPreviewable, entityType: EntityType, entityId: string) => {
        if (!isNullOrWhitespace(document.fileName)) {
            if (isPreviewSupported(document.fileName!)) {
                showPreview(document);
            } else {
                if (document.documentVersionId) {
                    downloadDocumentVersionByVersionId(document.documentId, document.documentVersionId, document.fileName!, entityType, entityId);
                } else {
                    await downloadDocumentVersion(document.documentId, document.dataId ?? "", document.fileName!, entityType, entityId);
                }
            }
        } else if (!isNullOrWhitespace(document.link)) {
            window.open(document.link, "_blank");
        }
    };

    const getPreviewHref = (document: DocumentPreviewable): string | undefined => {
        if (isNullOrWhitespace(document.fileName) || !isPreviewSupported(document.fileName!)) {
            return undefined;
        }
        const entityType = document.entityType ?? props.entityType;
        const entityId = document.entityId ?? props.entityId;
        if (entityType === undefined || isNullOrWhitespace(entityId)) {
            return undefined;
        }
        const documentWithEntityTitle: DocumentPreviewable = {
            ...document,
            entityTitle: document.entityTitle ?? props.entityTitle,
        };
        return buildDocumentPreviewUrl(documentWithEntityTitle, entityType, entityId!);
    };

    return {
        previewableDocuments,
        handleDocumentClick,
        handleDocumentWithTypeAndIdClick,
        getPreviewHref,
    };
}
